/**
 * @file     statistics.ts
 * @readme   ./README.md
 * @purpose  Shared deterministic statistical primitives for public data
 *           engines so medians, quantiles, coverage, and trend direction do
 *           not drift between live aggregates, snapshots, and page families.
 */

function finite(values: readonly number[]): number[] {
  return values.filter(Number.isFinite).sort((a, b) => a - b);
}

/** Linear-interpolated quantile, matching PostgreSQL percentile_cont. */
export function quantile(values: readonly number[], q: number): number | null {
  const sorted = finite(values);
  if (sorted.length === 0) return null;
  const clamped = Math.min(1, Math.max(0, q));
  const index = (sorted.length - 1) * clamped;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return sorted[lower]!;
  const weight = index - lower;
  return sorted[lower]! * (1 - weight) + sorted[upper]! * weight;
}

export function median(values: readonly number[]): number | null {
  return quantile(values, 0.5);
}

/** Currency/count aggregates are published as whole units. */
export function quantileRounded(values: readonly number[], q: number): number | null {
  const value = quantile(values, q);
  return value == null ? null : Math.round(value);
}

export function medianRounded(values: readonly number[]): number | null {
  return quantileRounded(values, 0.5);
}

export function coverageRatio(present: number, total: number): number | null {
  if (total <= 0) return null;
  return Math.min(1, Math.max(0, present / total));
}

export interface TrendSummary {
  first: number;
  last: number;
  absoluteChange: number;
  percentChange: number;
  direction: 'up' | 'down' | 'flat';
  domainMin: number;
  domainMax: number;
}

/**
 * Uses a minimum chart domain so tiny changes cannot fill an entire chart.
 * `flatThresholdRatio` controls the narrative, not whether exact values render.
 */
export function summarizeTrend(
  points: readonly (number | null)[],
  options: { flatThresholdRatio?: number; minimumDomainSpanRatio?: number } = {},
): TrendSummary | null {
  const values = points.filter((value): value is number => value != null && Number.isFinite(value));
  if (values.length < 2) return null;
  const first = values[0]!;
  const last = values.at(-1)!;
  const absoluteChange = last - first;
  const percentChange = first === 0 ? 0 : absoluteChange / Math.abs(first);
  const flatThreshold = options.flatThresholdRatio ?? 0.01;
  const direction = Math.abs(percentChange) < flatThreshold
    ? 'flat'
    : percentChange > 0
      ? 'up'
      : 'down';

  const actualMin = Math.min(...values);
  const actualMax = Math.max(...values);
  const midpoint = (actualMin + actualMax) / 2;
  const minimumSpan = Math.max(Math.abs(midpoint) * (options.minimumDomainSpanRatio ?? 0.04), 1);
  const domainSpan = Math.max(actualMax - actualMin, minimumSpan);

  return {
    first,
    last,
    absoluteChange,
    percentChange,
    direction,
    domainMin: midpoint - domainSpan / 2,
    domainMax: midpoint + domainSpan / 2,
  };
}

/** Counts missing weekly intervals without inventing replacement observations. */
export function missingWeeklyCaptures(capturedAt: readonly string[]): number {
  const times = capturedAt
    .map(Date.parse)
    .filter(Number.isFinite)
    .sort((a, b) => a - b);
  let missing = 0;
  for (let index = 1; index < times.length; index += 1) {
    const days = (times[index]! - times[index - 1]!) / 86_400_000;
    if (days > 10) missing += Math.max(1, Math.round(days / 7) - 1);
  }
  return missing;
}
